import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import ChatPreview from "./ChatPreview/ChatPreview.tsx";
import sharedChatMessageIdMapping from "../../../Utils/IdMappings/SharedChatMessageIdMapping.ts";
import { WEBSITE_URL } from "../../../Globals.ts";
import {useAppStore} from "../../../GlobalStore/GlobalStore.ts";
import {writeSharedChat} from "../../../Utils/db/sharedChats/writeSharedChat.ts";

interface SharePopupProps {}

const SharePopup: React.FC<SharePopupProps> = ({ }) => {
    const [tempId] = useState(uuidv4());
    const [isCreating, setIsCreating] = useState(false);
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { shareChatName, shareMessagesSequence, shareMessages } = dynamicStateObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const userObj = useAppStore((state: any) => state.userObj);

    const modalRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                setDynamicStateObj({visiblePopup: null});
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const createPermanentLink = async (tempId: string, chatName: string, msgs: any, msgsSequence: any) => {
        writeSharedChat(userObj.user, tempId, chatName, msgs, msgsSequence);
        return `${WEBSITE_URL}/share/${tempId}`;
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (message) {
            timer = setTimeout(() => {
                setMessage(null);
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [message]);

    const handleCreateLink = async () => {
        setIsCreating(true);
        try {
            const valuesArray = Object.values(sharedChatMessageIdMapping);

            const link = await createPermanentLink(tempId, shareChatName, valuesArray, shareMessagesSequence);
            await navigator.clipboard.writeText(link);
            setMessage({ text: 'Link copied to clipboard!', type: 'success' });
        } catch (error) {
            console.error('Error creating permanent link:', error);
            setMessage({ text: `Error creating link. Please try again.`, type: 'error' });
        }
        setIsCreating(false);
    };

    const handleCloseClick = () => {
        Object.keys(sharedChatMessageIdMapping).forEach(key => delete sharedChatMessageIdMapping[key]);
        setDynamicStateObj({visiblePopup: null, shareMessages: null, shareMessagesSequence: []});
    };

    return (
        <div className="fixed inset-0 bg-neutral-100/95 dark:bg-neutral-900/95 flex items-center justify-center p-4 z-50">
            <div ref={modalRef} className="bg-neutral-300 dark:bg-neutral-800/80 backdrop-blur-md rounded-xl shadow-2xl w-[98%] sm:w-11/12 max-w-2xl overflow-hidden border border-neutral-700/50 relative">
                <button
                    onClick={handleCloseClick}
                    className="absolute top-2 right-2 dark:text-neutral-200 dark:hover:text-white transition-colors duration-200"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mt-2 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="p-6">
                    <h2 className="text-2xl font-bold mb-4 dark:text-white text-center">Share Chat</h2>
                    <div className="mb-6 h-[500px] overflow-hidden flex items-center justify-center">
                        {shareMessages && shareMessagesSequence ? (
                            <ChatPreview rootNode={shareMessages} messagesSequence={shareMessagesSequence} />
                        ) : (
                            <div className="flex items-center justify-center">
                                <svg className="animate-spin h-8 w-8 text-gray-600 dark:text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        )}
                    </div>
                    {message && (
                        <p className={`mb-4 text-center ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                            {message.text}
                        </p>
                    )}
                    <div className="flex justify-center">
                        <button
                            onClick={handleCreateLink}
                            disabled={isCreating}
                            className="bg-green-600 px-6 py-3 rounded-full hover:bg-green-600 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <FontAwesomeIcon className={"text-black dark:text-white"} icon={faLink} />
                            <span className={"text-black dark:text-white"}> {isCreating ? 'Creating...' : 'Create Link'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SharePopup;